/**
 * 媒体节点上传共享逻辑 — image / audio / video 通用。
 *
 * 视频上传前校验大小和时长；音频上传前提取并缓存波形数据；
 * 上传成功后写入画布素材表并更新节点 assetRefs。
 * toolbar 空态上传和 corner-actions 素材替换都调用此方法。
 */
import { toast } from 'sonner'
import { uploadAsset } from '@/entities/asset'
import { updateNodeCustomData } from '@/entities/flow'
import { setNodeUploading } from '@/entities/node/lib'
import { MATERIAL_OP } from '@/shared/lib/op-registry'
import {
  cacheAudioWaveform,
  createNodeWaveformKey,
  extractAudioWaveformFromFile,
  promoteAudioWaveformCache,
} from '@/shared/lib/audio-wave/audio-waveform-cache'
import type { BaseNodeData } from '@/shared/lib/node-registry'
import { addCanvasAsset } from './canvas-assets-store'
import { mapUploadResultToCanvasAsset } from '../lib/canvas-asset-mappers'


/**
 * 上传媒体文件到节点：校验 → 上传 → 写入画布素材 → 设置素材态。
 *
 * 视频先校验大小和时长；音频先提取波形缓存到内存，上传成功后持久化。
 */
export async function uploadMediaToNode(args: {
  file: File
  nodeId: string
  nodeType: string
  nodeData: BaseNodeData
  projectId: string
  onProgress?: (percent: number) => void
}) {
  const { file, nodeId, nodeType, nodeData, projectId, onProgress } = args

  setNodeUploading(nodeId, true)

  try {
    if (nodeType === 'audio') {
      const waveformKey = createNodeWaveformKey(projectId, nodeId)
      const waveformEntry = await extractAudioWaveformFromFile(file, waveformKey)
      await cacheAudioWaveform(waveformEntry, { persist: false })
    }

    const result = await uploadAsset(file, {
      projectId,
      nodeId,
      onProgress: (_taskId, percent) => onProgress?.(percent)
    })
    const asset = mapUploadResultToCanvasAsset(result)

    if (nodeType === 'audio') {
      await promoteAudioWaveformCache(projectId, nodeId, asset.url)
    }

    addCanvasAsset(asset, nodeId)

    nodeData.op = MATERIAL_OP
    nodeData.title = file.name
    updateNodeCustomData(nodeId, { op: MATERIAL_OP, title: file.name })
  } catch (error) {
    toast.error(error instanceof Error ? error.message : '上传失败')
  } finally {
    setNodeUploading(nodeId, false)
  }
}
